import { comboKey } from "@/domain/profile-resolver/index";
import type { RapidFireMarks } from "@/domain/types";
import { isZone } from "@/domain/zones";
import type { SC2Hotkey, SC2Table } from "./parser/index";
import { hotkeysLayers } from "./parser/profile";
import { SC2_COMMANDS, type SC2Command, servingUnit } from "./sc2-registry";

// SC2 rendering aux: rapid-fire marks and zone reservation, derived from a parsed table.
// Binding resolution itself now flows through the one generic projector (projectLayers over a
// character-space Profile built by ingestSC2) - the old resolveSC2/placeCommand path is gone.

/**
 * The rapid-fire overlay (domain.md "Rapid-fire"): TargetChoose's
 * keyboard alternatives mark their keys, keyed by the exact combo each names.
 * Derived from the parsed table alone, independent of the command-card selection.
 * The curated registry diverts TargetChoose so it never becomes a Binding, and
 * marks are exact-combo only (no base fallback): a base mark must not render
 * while a modifier is held - a corner glyph has no provenance readout to keep it honest.
 * Marks are keyboard-only: rapid-fire rides key auto-repeat, and the
 * near-universal TargetChoose-on-LeftMouseButton lead is ordinary clicking, so a
 * zone never carries a mark even though the mouse alternative parses and places.
 */
export function rapidFireMarks(table: SC2Table): RapidFireMarks {
  const marks: RapidFireMarks = {};
  for (const hk of table.hotkeys.TargetChoose ?? []) {
    const combo = comboKey(hk.mods);
    const set = marks[combo] ?? new Set<string>();
    for (const code of hk.codes) {
      if (!isZone(code)) set.add(code);
    }
    if (set.size > 0) marks[combo] = set;
  }
  return marks;
}

/**
 * Every zone code any view of the table can place: the curated [Hotkeys] flatten
 * plus every registry command's card binding, regardless of the current selection.
 * Chip reservation reads this (reserved per Profile): switching
 * the command-card selection must never reflow the canvas. Curated-out ids and
 * the diverted TargetChoose never render, so they never reserve.
 */
export function placedZoneCodes(table: SC2Table): Set<string> {
  const zones = new Set<string>();
  const collect = (codes: string[]) => {
    for (const code of codes) {
      if (isZone(code)) zones.add(code);
    }
  };
  const flat = hotkeysLayers(table);
  for (const layer of [flat.base, ...Object.values(flat.mods ?? {})]) {
    collect(Object.keys(layer));
  }
  for (const entry of Object.values(SC2_COMMANDS)) {
    for (const hk of hotkeyFor(entry, table) ?? []) collect(hk.codes);
  }
  return zones;
}

/**
 * The [Commands] binding for a registry entry, in code space: the bucket servingUnit selects (the
 * entry's own unit, else _def - exact per-form lookup, see its doc). Used by placedZoneCodes; the
 * resolver selects the same bucket in token space (sc2-ingest servingValue).
 */
function hotkeyFor(entry: SC2Command, table: SC2Table): SC2Hotkey[] | undefined {
  const byUnit = table.commands[entry.key];
  if (!byUnit) return undefined;
  const unit = servingUnit(byUnit, entry.unit);
  return unit === undefined ? undefined : byUnit[unit];
}
